/**
 * RecordDetailPage - Full page view for a single record
 *
 * Atomic level: Page
 * Composes: RecordDetail, ConductScoreIndicator, Button
 *
 * Displays comprehensive record details including:
 * - Customer information and verbatim text
 * - Conduct score and labels (dissatisfaction, strain, harm, consumer duty)
 * - Regulatory flags (vulnerability)
 *
 * Following GO UI Design System patterns
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useRecord, useCollection, useReclassifyRecord } from '../../../hooks/useRecords';
import { RecordDetail } from './components';
import { Button } from '../../ui/button';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '../../ui/breadcrumb';
import { toast } from 'sonner';
import { reclassifyFeedback } from './RecordsPage';

interface RecordDetailPageProps {
  className?: string;
  /** Record ID - defaults to route param if not provided */
  recordId?: string;
  /** Enable vulnerability detection features */
  vulnerabilityDetectionEnabled?: boolean;
}

/**
 * RecordDetailPage Component
 *
 * Fetches and displays full record details with navigation and actions
 */
const RecordDetailPage: React.FC<RecordDetailPageProps> = ({
  className,
  recordId: propRecordId,
  vulnerabilityDetectionEnabled = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId: routeRecordId, collectionId } = useParams<{ recordId: string; collectionId: string }>();
  const recordId = propRecordId || routeRecordId;

  // Fetch record data
  const {
    data: record,
    isLoading,
    error,
  } = useRecord(recordId || '', { enabled: !!recordId });

  // Fetch collection data for breadcrumb
  const {
    data: collection,
  } = useCollection(collectionId || '', { enabled: !!collectionId });

  // Mutations
  const reclassifyMutation = useReclassifyRecord();

  // Reclassify state
  const [isReclassifying, setIsReclassifying] = useState(false);

  // Handle back navigation
  const handleBack = () => {
    const stateReferrer = (location.state as { referrer?: string } | null)?.referrer;
    if (stateReferrer) {
      navigate(stateReferrer);
      return;
    }
    // If we came from a collection, go back to it
    if (collectionId) {
      navigate(`/v4/records/${collectionId}`);
    } else {
      // Otherwise go to vault home
      navigate('/v4/records');
    }
  };

  // Handle reclassify
  const handleReclassify = async (recordId: string) => {
    setIsReclassifying(true);
    try {
      const outcome = await reclassifyMutation.mutateAsync({ recordId });
      const feedback = reclassifyFeedback(outcome?.status ?? 'reclassification_started');
      toast[feedback.level](feedback.message, { description: feedback.description });
    } catch (error) {
      console.error('Failed to reclassify record:', error);
      toast.error('Reclassification failed', {
        description: 'Please try again later.',
      });
    } finally {
      setIsReclassifying(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-background/80 px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Loading Record</h2>
          <p className="text-muted-foreground">Fetching record details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !record) {
    return (
      <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/70 bg-background/80 px-6 py-20 text-center shadow-[var(--shadow-card)]">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Record</h2>
          <p className="text-muted-foreground max-w-md">
            {error instanceof Error ? error.message : 'Record not found or an unexpected error occurred'}
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={handleBack}>
              Go Back
            </Button>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col overflow-y-auto bg-[var(--go-spring)] px-4 py-4 sm:px-6 sm:py-6', className)}>
      <div className="space-y-5">
        {/* Breadcrumb */}
        <div className="rounded-2xl border border-border/70 bg-background/90 px-4 py-4 shadow-[var(--shadow-card)] sm:px-5">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/v4/records">Collections</BreadcrumbLink>
              </BreadcrumbItem>
              {collection && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href={`/v4/records/${collectionId}`}>
                      {collection.collection.name}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {record.summary.slice(0, 50)}{record.summary.length > 50 ? '...' : ''}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Record Detail Content */}
        <div className="overflow-hidden rounded-2xl border border-border/70 bg-background/90 shadow-[var(--shadow-card)]">
          <RecordDetail
            record={record}
            vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
            onReclassify={handleReclassify}
            isReclassifying={isReclassifying}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export { RecordDetailPage };
export default RecordDetailPage;
